import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";

export enum IntegrationProviders {
  AIMS360 = "aims360",
  APPAREL_MAGIC = "apparelmagic",
  BRIGHTPEARL = "brightpearl",
  CLOUDX = "cloudx",
  FLEXPORT = "flexport",
  NETSUITE = "netsuite",
  SFOM = "sfom",
  SHIPBOB = "shipbob",
  SHIPHERO = "shiphero",
  TRACKSTAR = "trackstar",
  DEPOSCO = "deposco-old",
}

export enum TrackstarReturnProvider {
  SHIPSTREAM = "shipstream",
  BLUEBOX = "bluebox",
  SHIPMONK = "shipmonk",
  DEPOSCO = "deposco",
  NIMBLE = "nimble",
  WHIPLASH_RYDER = "whiplash",
  CIRRO = "cirro",
}

export const IntegrationProviderMerchantNames: Record<
  IntegrationProviders,
  string
> = {
  [IntegrationProviders.AIMS360]: "Aims360",
  [IntegrationProviders.APPAREL_MAGIC]: "Apparel Magic",
  [IntegrationProviders.BRIGHTPEARL]: "Brightpearl",
  [IntegrationProviders.CLOUDX]: "Bergen",
  [IntegrationProviders.FLEXPORT]: "Flexport",
  [IntegrationProviders.NETSUITE]: "NetSuite",
  [IntegrationProviders.SFOM]: "SFOM",
  [IntegrationProviders.SHIPBOB]: "ShipBob",
  [IntegrationProviders.SHIPHERO]: "ShipHero",
  [IntegrationProviders.TRACKSTAR]: "Trackstar",
  [IntegrationProviders.DEPOSCO]: "Deposco",
};

export const TrackstarReturnProviderMerchantNames: Record<
  TrackstarReturnProvider,
  string
> = {
  [TrackstarReturnProvider.SHIPSTREAM]: "ShipStream",
  [TrackstarReturnProvider.BLUEBOX]: "BlueBox",
  [TrackstarReturnProvider.SHIPMONK]: "ShipMonk",
  [TrackstarReturnProvider.DEPOSCO]: "Deposco",
  [TrackstarReturnProvider.NIMBLE]: "Nimble",
  [TrackstarReturnProvider.WHIPLASH_RYDER]: "Whiplash Ryder",
  [TrackstarReturnProvider.CIRRO]: "Cirro",
};

function isIntegrationProvider(
  name: IntegrationProviders | TrackstarReturnProvider,
): name is IntegrationProviders {
  return Object.values(IntegrationProviders).includes(
    name as IntegrationProviders,
  );
}

function isTrackstarReturnProvider(
  name: IntegrationProviders | TrackstarReturnProvider,
): name is TrackstarReturnProvider {
  return Object.values(TrackstarReturnProvider).includes(
    name as TrackstarReturnProvider,
  );
}

export const getMerchantFacingIntegrationName = (
  integrationName: IntegrationProviders | TrackstarReturnProvider,
): string => {
  if (isIntegrationProvider(integrationName)) {
    return IntegrationProviderMerchantNames[integrationName];
  }
  if (isTrackstarReturnProvider(integrationName)) {
    return TrackstarReturnProviderMerchantNames[integrationName];
  }

  // Fallback to original name if not found
  return integrationName;
};

export const getMerchantMessage = (errorCode: ReturnIntegrationErrorCode) => {
  switch (errorCode) {
    case Aims360IntegrationErrorCode.AIMS360_GENERAL_ERROR:
      return "The Aims360 integration failed to create the RMA. Please retry using the actions menu below, or create the RMA manually in your Aims360 system.";
    case Aims360IntegrationErrorCode.AIMS360_NO_INVOICES_FOUND:
      return "No Aims360 invoices were found for this order. Please verify that the order has been invoiced in Aims360, or create the RMA manually in your Aims360 system.";
    case FlexportIntegrationErrorCode.FLEXPORT_TRACKING_CODE_NOT_FOUND:
      return "Flexport could not find the tracking code for this shipment. Please retry using the actions menu below, or create the RMA manually in your Flexport system.";
    case FlexportIntegrationErrorCode.FLEXPORT_CARRIER_NOT_FOUND:
      return "Flexport could not find the carrier for this shipment. Please retry using the actions menu below, or create the RMA manually in your Flexport system.";
    case FlexportIntegrationErrorCode.FLEXPORT_INVALID_LOCATION:
      return "The return location is invalid or missing in your shipping and integration settings. Please add the location and retry using the actions menu below, or create the RMA manually in your Flexport system.";
    case FlexportIntegrationErrorCode.FLEXPORT_DUPLICATE_ENTRY:
      return "This return already exists in Flexport with the same tracking code. Check your Flexport system to confirm the return was created successfully, then dismiss this error.";
    case FlexportIntegrationErrorCode.FLEXPORT_PRODUCT_NOT_FOUND:
      return "One or more products in this return were not found in Flexport. Please retry using the actions menu below, excluding any non-physical items, or create the RMA manually in your Flexport system.";
    case FlexportIntegrationErrorCode.FLEXPORT_BAD_REQUEST:
      return "Flexport rejected the request to create the RMA, often due to non-physical products. Please retry using the actions menu below, excluding non-physical items, or create the RMA manually in your Flexport system.";
    case NetsuiteIntegrationErrorCode.NETSUITE_SALES_ORDER_NOT_FOUND:
      return "The sales order was not found in NetSuite. Please verify the order exists and retry using the actions menu below, or create the RMA manually in NetSuite.";
    case NetsuiteIntegrationErrorCode.NETSUITE_RETURN_AUTHORIZATION_CANNOT_BE_INITIALIZED:
      return "NetSuite could not initialize the return authorization, likely because the sales order is not yet fulfilled. Please fulfill the order and retry using the actions menu below, or create the RMA manually in NetSuite.";
    case NetsuiteIntegrationErrorCode.NETSUITE_RETURN_AUTHORIZATION_MUST_HAVE_LINE_ITEMS:
      return "No valid line items were found for the NetSuite return authorization. Please verify SKUs and retry using the actions menu below, or create the RMA manually in NetSuite.";
    case NetsuiteIntegrationErrorCode.NETSUITE_END_OF_GROUP_LINE_DELETE_ERROR:
      return "NetSuite encountered a group line error when creating the RMA. See this link for details: https://docs.oracle.com/en/cloud/saas/netsuite/ns-online-help/section_163306924686.html Retry using the actions menu below, or create the RMA manually in NetSuite.";
    case NetsuiteIntegrationErrorCode.NETSUITE_THIS_RECORD_ALREADY_EXISTS_ERROR:
      return "This return authorization already exists in NetSuite. Check your NetSuite system to confirm it was created successfully, then dismiss this error.";
    case NetsuiteIntegrationErrorCode.NETSUITE_INVALID_FIELD_VALUE_ERROR:
      return "NetSuite rejected invalid field values, likely due to inactive or matrix items. Please correct them and retry using the actions menu below, or create the RMA manually in NetSuite.";
    case NetsuiteIntegrationErrorCode.NETSUITE_INVALID_LOGIN_ATTEMPT_ERROR:
      return "Access to NetSuite has been lost. Please re-authenticate and retry using the actions menu below, or create the RMA manually in NetSuite.";
    case NetsuiteIntegrationErrorCode.NETSUITE_UNEXPECTED_SUITE_SCRIPT_ERROR:
      return "NetSuite encountered an unexpected SuiteScript error. Please contact support and retry using the actions menu below, or create the RMA manually in NetSuite.";
    case NetsuiteIntegrationErrorCode.NETSUITE_SALES_ORDER_MUST_BE_FULFILLED_ERROR:
      return "NetSuite requires the sales order to be fulfilled before creating a return authorization. Please fulfill the order and retry using the actions menu below.";
    case NetsuiteIntegrationErrorCode.NETSUITE_PERMISSION_VIOLATION_LISTS_TAX:
      return "NetSuite requires permission (lists -> tax details tab) to create a return authorization. Please check your NetSuite permissions and retry using the actions menu below, or create the RMA manually in NetSuite.";
    case SfomIntegrationErrorCode.SFOM_DIDNT_RECEIVE_RMA_ID:
      return "SFOM did not return an RMA ID. Please retry using the actions menu below, or create the RMA manually in your SFOM system.";
    case ShipbobIntegrationErrorCode.SHIPBOB_NO_ORDERS_FOUND_FOR_SHOPIFY_ORDER_ERROR:
      return "No ShipBob order was found for this Shopify order. Please update the reference ID and retry using the actions menu below, or create the RMA manually in ShipBob.";
    case ApparelMagicIntegrationErrorCode.APPAREL_MAGIC_ORDER_NOT_FOUND:
      return "No Apparel Magic order was found for this return. Please verify the order and retry using the actions menu below, or create the RMA manually in Apparel Magic.";
    case ApparelMagicIntegrationErrorCode.APPAREL_MAGIC_RA_NOT_DEFINED:
      return "The Apparel Magic RA number is not defined for this return. Please retry using the actions menu below, or create the RMA manually in Apparel Magic.";
    case ApparelMagicIntegrationErrorCode.APPAREL_MAGIC_MULTIPLE_ORDERS:
      return "This return contains multiple orders, which is not supported by Apparel Magic. Please create separate RMAs manually in Apparel Magic.";
    case ApparelMagicIntegrationErrorCode.APPAREL_MAGIC_NO_ITEMS_TO_RETURN:
      return "No returnable items were found in Apparel Magic for this return. Please retry using the actions menu below, or create the RMA manually in Apparel Magic.";
    case ApparelMagicIntegrationErrorCode.APPAREL_MAGIC_INSUFFICIENT_QUANTITY:
      return "The quantity of items in Apparel Magic is insufficient for this return. Please verify the quantity and retry using the actions menu below, or create the RMA manually in Apparel Magic.";
    case BrightpearlIntegrationErrorCode.BRIGHTPEARL_SALES_ORDER_ROW_NOT_FOUND:
      return "No Brightpearl sales order row was found for the returned products. Please verify the products and retry using the actions menu below, or create the RMA manually in Brightpearl.";
    case BrightpearlIntegrationErrorCode.BRIGHTPEARL_ORIGINAL_SALES_ORDER_NOT_FOUND:
      return "The original sales order was not found in Brightpearl. Please ensure it exists and retry using the actions menu below, or create it manually.";
    case CloudxIntegrationErrorCode.CLOUDX_ITEM_MASTER_ERROR:
      return "Bergen encountered an item master error. Please verify SKUs and retry using the actions menu below, or create the receiving ticket manually in Bergen.";
    case CloudxIntegrationErrorCode.CLOUDX_STATE_ERROR:
      return "Bergen requires a valid US state code for returns. Please retry using the actions menu below, or create the receiving ticket manually in Bergen.";
    case DeposcoIntegrationErrorCode.DEPOSCO_INTEGRATION_NOT_FOUND:
      return "Deposco integration credentials are missing or invalid. Please update them and retry using the actions menu below, or create the RMA manually in Deposco.";
    case ShipheroIntegrationErrorCode.SHIPHERO_ORDER_NOT_FOUND:
      return "No ShipHero order was found for this Shopify order. Please verify and retry using the actions menu below, or create the RMA manually in ShipHero.";
    case ShipheroIntegrationErrorCode.SHIPHERO_SKU_MISSING_ON_ORDER:
      return "One or more SKUs were not found on the original ShipHero order. Please verify and retry using the actions menu below, or create the RMA manually in ShipHero.";
    case TrackstarIntegrationErrorCode.ORDER_NOT_FOUND:
      return "The order was not found in the WMS. Please verify and retry using the actions menu below, or create the RMA manually in your WMS.";
    case TrackstarIntegrationErrorCode.ORDER_ID_NOT_FOUND_AFTER_CALLING_TRACKSTAR_ORDER_API:
      return "No order ID was found for this return in the WMS. Please verify the order exists and retry using the actions menu below, or create the RMA manually in your WMS.";
    case TrackstarIntegrationErrorCode.ORDER_ITEM_NOT_FOUND:
      return "One or more items from the return were not found in the WMS. Please verify and retry using the actions menu below, or create the RMA manually in your WMS.";
    case TrackstarIntegrationErrorCode.WHIPLASH_RYDER_INVENTORY_ITEM_NOT_FOUND:
      return "One or more inventory items were not found in Whiplash Ryder WMS. Make sure that inventory for the SKUs in this return are properly imported into Whiplash Ryder WMS. Please verify and retry using the actions menu below, or create the RMA manually in your WMS.";
    case TrackstarIntegrationErrorCode.RETURN_ALREADY_EXISTS:
      return "This return already exists in the WMS. Please check your system to confirm it was created successfully, then dismiss this error.";
    case TrackstarIntegrationErrorCode.NO_WAREHOUSE_FOUND_FOR_SHIPMENT:
      return "No warehouse was found for this return shipment. Please add the warehouse and retry using the actions menu below, or create the RMA manually in your WMS.";
    case TrackstarIntegrationErrorCode.NO_ORDER_ID_FOUND_FOR_RETURN:
      return "No order ID was found for this return in the WMS. Please verify the order exists and retry using the actions menu below, or create the RMA manually in your WMS.";
    case TrackstarIntegrationErrorCode.ORDER_NOT_FOUND_FOR_CUSTOMER_NUMBER_OR_PO_NUMBER:
      return "The order was not found in the WMS. Please verify the order exists and retry using the actions menu below, or create the RMA manually in your WMS.";
    case TrackstarIntegrationErrorCode.SHIPSTREAM_DELIVERY_ALREADY_EXISTS:
      return "The ShipStream delivery already exists. Please check your system to confirm it was created successfully, then dismiss this error.";
    case UnknownErrorCode.UNKNOWN_ERROR:
      return "An unexpected error occurred during RMA creation. Please retry using the actions menu below, or create the RMA manually in your WMS. Contact support if this persists.";
    case GeneralErrorCode.LABEL_REQUIRED:
      return "A return shipping label is required before the RMA can be created. Please generate a return label and retry using the actions menu below, or create the RMA manually in your system.";
    default:
      assertNever(errorCode);
  }
};

// Individual integration error code enums
export enum Aims360IntegrationErrorCode {
  AIMS360_GENERAL_ERROR = "AIMS360_GENERAL_ERROR",
  AIMS360_NO_INVOICES_FOUND = "AIMS360_NO_INVOICES_FOUND",
}

export enum ApparelMagicIntegrationErrorCode {
  APPAREL_MAGIC_ORDER_NOT_FOUND = "APPAREL_MAGIC_ORDER_NOT_FOUND",
  APPAREL_MAGIC_RA_NOT_DEFINED = "APPAREL_MAGIC_RA_NOT_DEFINED",
  APPAREL_MAGIC_MULTIPLE_ORDERS = "APPAREL_MAGIC_MULTIPLE_ORDERS",
  APPAREL_MAGIC_NO_ITEMS_TO_RETURN = "APPAREL_MAGIC_NO_ITEMS_TO_RETURN",
  APPAREL_MAGIC_INSUFFICIENT_QUANTITY = "APPAREL_MAGIC_INSUFFICIENT_QUANTITY",
}

export enum BrightpearlIntegrationErrorCode {
  BRIGHTPEARL_SALES_ORDER_ROW_NOT_FOUND = "BRIGHTPEARL_SALES_ORDER_ROW_NOT_FOUND",
  BRIGHTPEARL_ORIGINAL_SALES_ORDER_NOT_FOUND = "BRIGHTPEARL_ORIGINAL_SALES_ORDER_NOT_FOUND",
}

export enum CloudxIntegrationErrorCode {
  CLOUDX_ITEM_MASTER_ERROR = "CLOUDX_ITEM_MASTER_ERROR",
  CLOUDX_STATE_ERROR = "CLOUDX_STATE_ERROR",
}

export enum DeposcoIntegrationErrorCode {
  DEPOSCO_INTEGRATION_NOT_FOUND = "DEPOSCO_INTEGRATION_NOT_FOUND",
}

export enum FlexportIntegrationErrorCode {
  FLEXPORT_TRACKING_CODE_NOT_FOUND = "FLEXPORT_TRACKING_CODE_NOT_FOUND",
  FLEXPORT_CARRIER_NOT_FOUND = "FLEXPORT_CARRIER_NOT_FOUND",
  FLEXPORT_INVALID_LOCATION = "FLEXPORT_INVALID_LOCATION",
  FLEXPORT_DUPLICATE_ENTRY = "FLEXPORT_DUPLICATE_ENTRY",
  FLEXPORT_PRODUCT_NOT_FOUND = "FLEXPORT_PRODUCT_NOT_FOUND",
  FLEXPORT_BAD_REQUEST = "FLEXPORT_BAD_REQUEST",
}

export enum NetsuiteIntegrationErrorCode {
  NETSUITE_SALES_ORDER_NOT_FOUND = "NETSUITE_SALES_ORDER_NOT_FOUND",
  NETSUITE_RETURN_AUTHORIZATION_CANNOT_BE_INITIALIZED = "NETSUITE_RETURN_AUTHORIZATION_CANNOT_BE_INITIALIZED",
  NETSUITE_RETURN_AUTHORIZATION_MUST_HAVE_LINE_ITEMS = "NETSUITE_RETURN_AUTHORIZATION_MUST_HAVE_LINE_ITEMS",
  NETSUITE_END_OF_GROUP_LINE_DELETE_ERROR = "NETSUITE_END_OF_GROUP_LINE_DELETE_ERROR",
  NETSUITE_THIS_RECORD_ALREADY_EXISTS_ERROR = "NETSUITE_THIS_RECORD_ALREADY_EXISTS_ERROR",
  NETSUITE_INVALID_FIELD_VALUE_ERROR = "NETSUITE_INVALID_FIELD_VALUE_ERROR",
  NETSUITE_INVALID_LOGIN_ATTEMPT_ERROR = "NETSUITE_INVALID_LOGIN_ATTEMPT_ERROR",
  NETSUITE_UNEXPECTED_SUITE_SCRIPT_ERROR = "NETSUITE_UNEXPECTED_SUITE_SCRIPT_ERROR",
  NETSUITE_SALES_ORDER_MUST_BE_FULFILLED_ERROR = "NETSUITE_SALES_ORDER_MUST_BE_FULFILLED_ERROR",
  NETSUITE_PERMISSION_VIOLATION_LISTS_TAX = "NETSUITE_PERMISSION_VIOLATION_LISTS_TAX",
}

export enum SfomIntegrationErrorCode {
  SFOM_DIDNT_RECEIVE_RMA_ID = "SFOM_DIDNT_RECEIVE_RMA_ID",
}

export enum ShipbobIntegrationErrorCode {
  SHIPBOB_NO_ORDERS_FOUND_FOR_SHOPIFY_ORDER_ERROR = "SHIPBOB_NO_ORDERS_FOUND_FOR_SHOPIFY_ORDER_ERROR",
}

export enum ShipheroIntegrationErrorCode {
  SHIPHERO_ORDER_NOT_FOUND = "SHIPHERO_ORDER_NOT_FOUND",
  SHIPHERO_SKU_MISSING_ON_ORDER = "SHIPHERO_SKU_MISSING_ON_ORDER",
}

export enum TrackstarIntegrationErrorCode {
  SHIPSTREAM_DELIVERY_ALREADY_EXISTS = "SHIPSTREAM_DELIVERY_ALREADY_EXISTS",
  ORDER_ID_NOT_FOUND_AFTER_CALLING_TRACKSTAR_ORDER_API = "ORDER_ID_NOT_FOUND_AFTER_CALLING_TRACKSTAR_ORDER_API",
  ORDER_NOT_FOUND = "ORDER_NOT_FOUND",
  ORDER_ITEM_NOT_FOUND = "ORDER_ITEM_NOT_FOUND",
  WHIPLASH_RYDER_INVENTORY_ITEM_NOT_FOUND = "WHIPLASH_RYDER_INVENTORY_ITEM_NOT_FOUND",
  RETURN_ALREADY_EXISTS = "RETURN_ALREADY_EXISTS",
  NO_WAREHOUSE_FOUND_FOR_SHIPMENT = "NO_WAREHOUSE_FOUND_FOR_SHIPMENT",
  NO_ORDER_ID_FOUND_FOR_RETURN = "NO_ORDER_ID_FOUND_FOR_RETURN",
  ORDER_NOT_FOUND_FOR_CUSTOMER_NUMBER_OR_PO_NUMBER = "ORDER_NOT_FOUND_FOR_CUSTOMER_NUMBER_OR_PO_NUMBER",
}

export enum GeneralErrorCode {
  LABEL_REQUIRED = "LABEL_REQUIRED",
}

export enum UnknownErrorCode {
  UNKNOWN_ERROR = "UNKNOWN_ERROR",
}

export enum ReturnIntegrationSyncResultType {
  NoSetUp = "NO_SET_UP",
  Failure = "FAILURE",
  AlreadyExists = "ALREADY_EXISTS",
  LogicallyNotNeeded = "LOGICALLY_NOT_NEEDED",
  LabelRequired = "LABEL_REQUIRED",
  Success = "SUCCESS",
}

// Zod schemas for individual integration error codes
export const Aims360IntegrationErrorCodeZodSchema = createEnum(
  Aims360IntegrationErrorCode,
  { enumName: "Aims360IntegrationErrorCode" },
);
export const ApparelMagicIntegrationErrorCodeZodSchema = createEnum(
  ApparelMagicIntegrationErrorCode,
  { enumName: "ApparelMagicIntegrationErrorCode" },
);
export const BrightpearlIntegrationErrorCodeZodSchema = createEnum(
  BrightpearlIntegrationErrorCode,
  { enumName: "BrightpearlIntegrationErrorCode" },
);
export const CloudxIntegrationErrorCodeZodSchema = createEnum(
  CloudxIntegrationErrorCode,
  { enumName: "CloudxIntegrationErrorCode" },
);
export const DeposcoIntegrationErrorCodeZodSchema = createEnum(
  DeposcoIntegrationErrorCode,
  { enumName: "DeposcoIntegrationErrorCode" },
);
export const FlexportIntegrationErrorCodeZodSchema = createEnum(
  FlexportIntegrationErrorCode,
  { enumName: "FlexportIntegrationErrorCode" },
);
export const NetsuiteIntegrationErrorCodeZodSchema = createEnum(
  NetsuiteIntegrationErrorCode,
  { enumName: "NetsuiteIntegrationErrorCode" },
);
export const SfomIntegrationErrorCodeZodSchema = createEnum(
  SfomIntegrationErrorCode,
  { enumName: "SfomIntegrationErrorCode" },
);
export const ShipbobIntegrationErrorCodeZodSchema = createEnum(
  ShipbobIntegrationErrorCode,
  { enumName: "ShipbobIntegrationErrorCode" },
);
export const ShipheroIntegrationErrorCodeZodSchema = createEnum(
  ShipheroIntegrationErrorCode,
  { enumName: "ShipheroIntegrationErrorCode" },
);
export const TrackstarIntegrationErrorCodeZodSchema = createEnum(
  TrackstarIntegrationErrorCode,
  { enumName: "TrackstarIntegrationErrorCode" },
);
export const GeneralErrorCodeZodSchema = createEnum(GeneralErrorCode, {
  enumName: "GeneralErrorCode",
});
export const UnknownErrorCodeZodSchema = createEnum(UnknownErrorCode, {
  enumName: "UnknownErrorCode",
});

// Union type for all integration error codes
export type ReturnIntegrationErrorCode =
  | ShipheroIntegrationErrorCode
  | BrightpearlIntegrationErrorCode
  | FlexportIntegrationErrorCode
  | NetsuiteIntegrationErrorCode
  | ShipbobIntegrationErrorCode
  | SfomIntegrationErrorCode
  | ApparelMagicIntegrationErrorCode
  | Aims360IntegrationErrorCode
  | CloudxIntegrationErrorCode
  | DeposcoIntegrationErrorCode
  | TrackstarIntegrationErrorCode
  | GeneralErrorCode
  | UnknownErrorCode;

// Zod schema for the union type
export const ReturnIntegrationErrorCodeZodSchema = z.union([
  ShipheroIntegrationErrorCodeZodSchema,
  BrightpearlIntegrationErrorCodeZodSchema,
  FlexportIntegrationErrorCodeZodSchema,
  NetsuiteIntegrationErrorCodeZodSchema,
  ShipbobIntegrationErrorCodeZodSchema,
  SfomIntegrationErrorCodeZodSchema,
  ApparelMagicIntegrationErrorCodeZodSchema,
  Aims360IntegrationErrorCodeZodSchema,
  CloudxIntegrationErrorCodeZodSchema,
  DeposcoIntegrationErrorCodeZodSchema,
  TrackstarIntegrationErrorCodeZodSchema,
  GeneralErrorCodeZodSchema,
  UnknownErrorCodeZodSchema,
]);

export const ReturnIntegrationErrorCodeValues = [
  ...Object.values(ShipheroIntegrationErrorCode),
  ...Object.values(BrightpearlIntegrationErrorCode),
  ...Object.values(FlexportIntegrationErrorCode),
  ...Object.values(NetsuiteIntegrationErrorCode),
  ...Object.values(ShipbobIntegrationErrorCode),
  ...Object.values(SfomIntegrationErrorCode),
  ...Object.values(ApparelMagicIntegrationErrorCode),
  ...Object.values(Aims360IntegrationErrorCode),
  ...Object.values(CloudxIntegrationErrorCode),
  ...Object.values(DeposcoIntegrationErrorCode),
  ...Object.values(TrackstarIntegrationErrorCode),
  ...Object.values(GeneralErrorCode),
  ...Object.values(UnknownErrorCode),
];
export type ReturnIntegrationErrorCodeValues =
  (typeof ReturnIntegrationErrorCodeValues)[number];

export const ReturnIntegrationSyncResultTypeZodSchema = createEnum(
  ReturnIntegrationSyncResultType,
  { enumName: "ReturnIntegrationSyncResultType" },
);

// Following the pattern from the codebase where we have both the underscore version and the typed version
const ReturnIntegrationErrorCodeZodSchema_ =
  ReturnIntegrationErrorCodeZodSchema;
export const ReturnIntegrationErrorCodeZodSchemaFinal: z.ZodType<ReturnIntegrationErrorCode> =
  ReturnIntegrationErrorCodeZodSchema_;
export type ReturnIntegrationErrorCodeZod = z.infer<
  typeof ReturnIntegrationErrorCodeZodSchema_
>;

export enum NetSuiteTransactionType {
  CASH_SALE = "cash_sale",
  SALES_ORDER = "sales_order",
  BOTH = "both",
}

export const NetSuiteTransactionTypeZodSchema = createEnum(
  NetSuiteTransactionType,
  { enumName: "NetSuiteTransactionType" },
);

export enum NetSuiteItemValueType {
  CREDIT = "credit",
  REFUND = "refund",
  TOTAL = "total",
  TOTAL_PRE_DISCOUNT = "total-pre-discount",
}

export const NetSuiteItemValueTypeZodSchema = createEnum(
  NetSuiteItemValueType,
  { enumName: "NetSuiteItemValueType" },
);
